import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, Phone, UserPlus, FileText, TrendingUp, Calendar, Filter, Building, DollarSign } from 'lucide-react';
import { 
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  isWithinInterval, startOfDay, endOfDay
} from 'date-fns';

const Dashboard = ({ 
  goals = {}, 
  proposals = [],
  products = [],
  quarterlyGoals = {},
  onFilterChange, 
  dateFilter: externalDateFilter 
}) => {
  const [dateFilter, setDateFilter] = useState(externalDateFilter || 'currentWeek');

  // Sincronizar con el filtro externo si cambia
  React.useEffect(() => {
    if (externalDateFilter && externalDateFilter !== dateFilter) {
      setDateFilter(externalDateFilter);
    }
  }, [externalDateFilter]);

  const filterOptions = [
    { value: 'currentWeek', label: 'Semana Actual' },
    { value: 'lastWeek', label: 'Semana Pasada' },
    { value: 'currentMonth', label: 'Mes Actual' },
    { value: 'lastMonth', label: 'Mes Pasado' }
  ];

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };
  // Función para obtener el sufijo según el filtro
  const getPeriodSuffix = () => {
    const suffixes = {
      'currentWeek': 'Semanales',
      'lastWeek': 'Semanales',
      'currentMonth': 'Mensuales',
      'lastMonth': 'Mensuales'
    };
    return suffixes[dateFilter] || 'Semanales';
  };

  const periodSuffix = getPeriodSuffix();

  // Estados de propuestas a incluir para facturación
  // Incluir desde "Facturado" en adelante, excepto "Rechazada"
  const targetStatuses = ['Facturado', 'Pendiente de Cobro', 'Pagado Anticipo', 'Pago Total'];

  // Función auxiliar para verificar si una propuesta debe incluirse en la facturación
  const shouldIncludeInBilling = (proposal) => {
    // Excluir explícitamente "Rechazada"
    if (proposal.status === 'Rechazada') return false;
    // Incluir si está en los estados objetivo
    const included = targetStatuses.includes(proposal.status);
    
    // Log para depuración
    if (!included && proposal.status) {
      console.log('Propuesta excluida de facturación:', {
        proposalId: proposal.proposalId || proposal.proposal_id,
        status: proposal.status,
        targetStatuses
      });
    }
    
    return included;
  };

  // Calcular facturación del mes corriente
  const currentMonthBilling = useMemo(() => {
    const now = new Date();
    const monthStart = startOfDay(startOfMonth(now));
    const monthEnd = endOfDay(endOfMonth(now));

    const filtered = proposals.filter(proposal => {
      if (!shouldIncludeInBilling(proposal)) return false;
      
      const proposalDate = proposal.approvedDate 
        ? new Date(proposal.approvedDate)
        : (proposal.approved_date 
          ? new Date(proposal.approved_date)
          : (proposal.created_at ? new Date(proposal.created_at) : null));
      
      if (!proposalDate || isNaN(proposalDate.getTime())) {
        console.log('Propuesta sin fecha válida (mes):', {
          proposalId: proposal.proposalId || proposal.proposal_id,
          status: proposal.status,
          approvedDate: proposal.approvedDate,
          approved_date: proposal.approved_date,
          created_at: proposal.created_at
        });
        return false;
      }
      
      const inRange = isWithinInterval(proposalDate, { start: monthStart, end: monthEnd });
      if (!inRange) {
        console.log('Propuesta fuera del rango del mes:', {
          proposalId: proposal.proposalId || proposal.proposal_id,
          status: proposal.status,
          proposalDate: proposalDate.toISOString(),
          monthStart: monthStart.toISOString(),
          monthEnd: monthEnd.toISOString()
        });
      }
      
      return inRange;
    });

    const total = filtered.reduce((sum, proposal) => {
      const proposalTotal = parseFloat(proposal.totalAmount || proposal.total_amount || 0);
      return sum + proposalTotal;
    }, 0);

    return total;
  }, [proposals]);

  // Calcular facturación del trimestre corriente
  const currentQuarterBilling = useMemo(() => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const quarterStart = startOfDay(startOfQuarter(new Date(now.getFullYear(), quarter * 3, 1)));
    const quarterEnd = endOfDay(endOfQuarter(new Date(now.getFullYear(), quarter * 3, 1)));

    const filtered = proposals.filter(proposal => {
      if (!shouldIncludeInBilling(proposal)) return false;
      
      const proposalDate = proposal.approvedDate 
        ? new Date(proposal.approvedDate)
        : (proposal.approved_date 
          ? new Date(proposal.approved_date)
          : (proposal.created_at ? new Date(proposal.created_at) : null));
      
      if (!proposalDate || isNaN(proposalDate.getTime())) {
        console.log('Propuesta sin fecha válida (trimestre):', {
          proposalId: proposal.proposalId || proposal.proposal_id,
          status: proposal.status,
          approvedDate: proposal.approvedDate,
          approved_date: proposal.approved_date,
          created_at: proposal.created_at
        });
        return false;
      }
      
      const inRange = isWithinInterval(proposalDate, { start: quarterStart, end: quarterEnd });
      if (!inRange) {
        console.log('Propuesta fuera del rango del trimestre:', {
          proposalId: proposal.proposalId || proposal.proposal_id,
          status: proposal.status,
          proposalDate: proposalDate.toISOString(),
          quarterStart: quarterStart.toISOString(),
          quarterEnd: quarterEnd.toISOString()
        });
      }
      
      return inRange;
    });

    const total = filtered.reduce((sum, proposal) => {
      const proposalTotal = parseFloat(proposal.totalAmount || proposal.total_amount || 0);
      return sum + proposalTotal;
    }, 0);

    console.log('Facturación trimestral calculada:', {
      total,
      propuestasIncluidas: filtered.length,
      propuestasTotal: proposals.length,
      quarter: quarter + 1
    });

    return total;
  }, [proposals]);

  // Obtener trimestre actual y objetivo
  const getCurrentQuarter = () => {
    const now = new Date();
    return Math.floor(now.getMonth() / 3) + 1; // Q1, Q2, Q3, Q4
  };

  const currentQuarter = getCurrentQuarter();
  const currentQuarterGoal = quarterlyGoals[`q${currentQuarter}`] || quarterlyGoals[`Q${currentQuarter}`] || 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const goalCards = [
    {
      title: `Reuniones ${periodSuffix}`,
      current: goals.meetings?.current || 0,
      target: goals.meetings?.target || 6,
      icon: Users,
      solidColor: 'bg-blue-600',
      textColor: 'text-blue-700'
    },
    {
      title: `Contactos ${periodSuffix}`,
      current: goals.contacts?.current || 0,
      target: goals.contacts?.target || 250,
      icon: Phone,
      solidColor: 'bg-green-600',
      textColor: 'text-green-700'
    },
    {
      title: `Nuevos Leads ${periodSuffix}`,
      current: goals.leads?.current || 0,
      target: goals.leads?.target || 5,
      icon: UserPlus,
      solidColor: 'bg-purple-600',
      textColor: 'text-purple-700'
    },
    {
      title: 'Facturación Mes Corriente',
      current: currentMonthBilling,
      target: 0,
      icon: DollarSign,
      solidColor: 'bg-emerald-600',
      textColor: 'text-emerald-700',
      showProgress: false,
      isCurrency: true
    },
    {
      title: `Facturación Q${currentQuarter}`,
      current: currentQuarterBilling,
      target: currentQuarterGoal,
      icon: TrendingUp,
      solidColor: 'bg-blue-600',
      textColor: 'text-blue-700',
      showProgress: true,
      isCurrency: true
    }
  ];

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  return (
    <div className="space-y-6">
      <motion.div
        className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-xl">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Dashboard y Objetivos
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={dateFilter}
              onChange={handleFilterChange}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium text-gray-700 cursor-pointer"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {goalCards.map((goal, index) => {
            const progress = getProgressPercentage(goal.current, goal.target);
            const Icon = goal.icon;
            
            return (
              <motion.div
                key={goal.title}
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 ${goal.solidColor} rounded-xl`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className={`text-2xl font-bold ${goal.textColor}`}>
                    {goal.isCurrency ? formatCurrency(goal.current) : goal.current}
                  </span>
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-3 text-base">{goal.title}</h3>
                
                {goal.showProgress !== false && goal.target > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                      <span>Objetivo: {goal.isCurrency ? formatCurrency(goal.target) : goal.target}</span>
                      <span className="font-semibold text-gray-900">{Math.round(progress)}%</span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <motion.div 
                        className={`h-2.5 ${goal.solidColor} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                      />
                    </div>
                  </>
                )}
                {goal.showProgress === false && (
                  <div className="text-sm text-gray-500 mt-2">
                    <span>Total facturado</span>
                  </div>
                )}
                {goal.showProgress !== false && goal.target === 0 && (
                  <div className="text-sm text-gray-500 mt-2">
                    <span>Configura el objetivo en Configuración</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

    </div>
  );
};

export default Dashboard;